import React, { useState, useEffect } from "react";
import {
  useAuth,
  useTenantsState,
  useAuthActions,
  signOutUser,
} from "../../auth/workosAuth";
import { User, LogOut } from "react-feather";
import { setSession } from "../../utils/jwt";
import {
  MenuItem,
  Menu,
  Typography,
  Tooltip,
  Divider,
  Box,
} from "@mui/material";

const SidebarProfileDropdown = () => {
  const { switchTenant } = useAuthActions();
  const [tenant, setTenant] = useState(localStorage.getItem("activeTenantId"));
  const [profileImage, setProfileImage] = useState();
  const { user } = useAuth();
  const { tenants } = useTenantsState();
  const [sortedTenants, setSortedTenants] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (tenants.length > 0) {
      const sorted = [...tenants].sort((a, b) => a.name.localeCompare(b.name));
      setSortedTenants(sorted);
    }
  }, [tenants]);

  useEffect(() => {
    if (user?.profilePictureUrl) {
      setProfileImage(user.profilePictureUrl);
    }
  }, [user?.profilePictureUrl]);

  useEffect(() => {
    const activeTenantId = localStorage.getItem("activeTenantId");
    if (activeTenantId) {
      setTenant(activeTenantId);
    }
  }, []);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTenantChange = async (newTenantId) => {
    try {
      localStorage.removeItem("timezone");
      localStorage.removeItem("logo");
      localStorage.removeItem("dwh_schemas");
      localStorage.removeItem("selectedDatawarehouse");
      localStorage.removeItem("last_successful_query");
      setSession(null);
      localStorage.setItem("activeBranch", "main");
      localStorage.setItem("activeTenantId", newTenantId);
      setTenant(newTenantId);
      await switchTenant({ tenantId: newTenantId });
      // WorkOS switchToOrganization swaps the access token silently (no reload),
      // unlike Frontegg's switchTenant which reloaded the app. Force a full
      // navigation home so every org-scoped view re-fetches under the new org.
      window.location.assign("/");
    } catch (error) {
      console.error("Error switching tenant:", error);
    }
  };

  const signOut = () => {
    signOutUser();
  };

  return (
    <>
      <Tooltip
        title="Profile"
        placement="right"
        arrow
        slotProps={{
          arrow: { sx: { color: "#546e7a" } },
          tooltip: {
            sx: {
              backgroundColor: "#546e7a",
              fontSize: "11px",
              padding: "6px 12px",
              borderRadius: "6px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            },
          },
        }}
      >
        <Box
          onClick={handleMenu}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: "50%",
            cursor: "pointer",
            overflow: "hidden",
            border: "2px solid rgba(255,255,255,0.2)",
            transition: "all 0.15s ease-in-out",
            "&:hover": {
              borderColor: "rgba(255,255,255,0.5)",
              transform: "scale(1.04)",
            },
          }}
        >
          {profileImage ? (
            <img
              src={profileImage}
              alt={user?.name || "Avatar"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <User size={18} color="rgba(233, 236, 239, 0.7)" />
          )}
        </Box>
      </Tooltip>

      <Menu
        id="sidebar-profile-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e9ecef",
            minWidth: "240px",
            overflow: "hidden",
            ml: 1.5,
          },
          "& .MuiList-root": {
            padding: 0,
          },
        }}
      >
        {/* User Header Section */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {profileImage ? (
              <img
                src={profileImage}
                alt={user?.name || "Avatar"}
                style={{
                  borderRadius: "50%",
                  border: "2px solid #fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  width: 44,
                  height: 44,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  backgroundColor: "#e9ecef",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <User size={24} color="#6c757d" />
              </Box>
            )}
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#293042",
                  lineHeight: 1.3,
                }}
              >
                {user?.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#6c757d",
                  lineHeight: 1.3,
                }}
              >
                {user?.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Organizations Section */}
        {sortedTenants.length > 0 && (
          <>
            <Divider sx={{ my: 0.25 }} />
            <Typography
              sx={{
                px: 2,
                py: 0.5,
                fontSize: "10px",
                fontWeight: 600,
                color: "#adb5bd",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Organizations
            </Typography>
            {sortedTenants.map((tenantItem) => (
              <MenuItem
                key={tenantItem.name}
                onClick={() => {
                  handleTenantChange(tenantItem.tenantId);
                  handleClose();
                }}
                sx={{
                  px: 2,
                  py: 0.5,
                  color: tenantItem.tenantId === tenant ? "#3651FE" : "#495057",
                  fontWeight: tenantItem.tenantId === tenant ? 600 : 400,
                  gap: 1,
                  alignItems: "center",
                  fontSize: "13px",
                  transition: "background-color 0.15s",
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                  },
                }}
              >
                {tenantItem.name}
              </MenuItem>
            ))}
          </>
        )}

        {/* Sign Out at bottom */}
        <Divider sx={{ my: 0.25 }} />
        <MenuItem
          onClick={signOut}
          sx={{
            px: 2,
            py: 0.75,
            mb: 0.5,
            color: "#495057",
            gap: 1,
            alignItems: "center",
            fontSize: "13px",
            transition: "all 0.15s",
            "&:hover": {
              backgroundColor: "#fff5f5",
              color: "#dc3545",
            },
          }}
        >
          <LogOut size={16} />
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
};

export default SidebarProfileDropdown;
